import {
    ApiAmaxPayLegalEntityType,
    ApiAmaxPayPaymentType,
    CountryIsoCode,
    FieldWithRequirement,
    PaymentDetailsLegalEntityType,
    PaymentDetailsPaymentType,
    PaymentDetailsRequirementsItem,
} from 'shared/data/webApp/v2';

import {
    chapsPaymentMethod,
    fasterPaymentsPaymentMethod,
} from './EditCounterpartyPaymentDetailsPopupContent.constants';
import { FormValues } from './EditCounterpartyPaymentDetailsPopupContent.types';

// TODO: this is temp solution, later we will receive countries from BE
// This constant array is placed here and not in constants file to prevent circular dependencies
const restrictedCountries = [
    // Restricted countries by CurrencyCloud
    CountryIsoCode.BY,
    CountryIsoCode.CU,
    CountryIsoCode.IR,
    CountryIsoCode.KZ,
    CountryIsoCode.LY,
    CountryIsoCode.MM,
    CountryIsoCode.KP,
    CountryIsoCode.RU,
    CountryIsoCode.SS,
    CountryIsoCode.SD,
    CountryIsoCode.SY,
    CountryIsoCode.VE,
    // Not actual countries like some dependant territories or not/partially recognized
    CountryIsoCode.AQ,
    CountryIsoCode.AX,
    CountryIsoCode.BQ,
    CountryIsoCode.BV,
    CountryIsoCode.CC,
    CountryIsoCode.CX,
    CountryIsoCode.EH,
    CountryIsoCode.GS,
    CountryIsoCode.HM,
    CountryIsoCode.IM,
    CountryIsoCode.PN,
    CountryIsoCode.SJ,
    CountryIsoCode.SX,
    CountryIsoCode.TF,
    CountryIsoCode.UM,
] as const;

export const isRestrictedCountry = (country: CountryIsoCode): country is (typeof restrictedCountries)[number] =>
    restrictedCountries.findIndex((item) => item === country) !== -1;

export const getPaymentDetailsTransferData = (
    values: FormValues,
    paymentDetailsRequirements: PaymentDetailsRequirementsItem[] | undefined
) => {
    const neededRequirements = paymentDetailsRequirements?.find(
        (item) =>
            item.payNetworkName === values.paymentMethod &&
            item.legalEntityTypeField.legalEntityType === values.legalEntity.legalEntityType
    );

    const paymentType =
        neededRequirements?.paymentTypeField.paymentType === PaymentDetailsPaymentType.Priority
            ? ApiAmaxPayPaymentType.priority
            : ApiAmaxPayPaymentType.regular;

    const defaultCompanyName =
        values.legalEntity.legalEntityType === PaymentDetailsLegalEntityType.Company
            ? values.bankAccount.bankAccountName
            : undefined;

    return {
        ...values,
        bankAccount: {
            ...values.bankAccount,
            countryIsoCode: values.payeeBankCountry,
            currencyIsoCode: values.currencyCode,
        },
        legalEntity: {
            ...values.legalEntity,
            companyName: values.legalEntity.companyName ?? defaultCompanyName,
            addressInfo: {
                ...values.legalEntity.addressInfo,
                country: values.payerBankCountry,
            },
            legalEntityType:
                values.legalEntity.legalEntityType === PaymentDetailsLegalEntityType.Company
                    ? ApiAmaxPayLegalEntityType.company
                    : ApiAmaxPayLegalEntityType.individual,
        },
        paymentType,
    };
};

export const getRequirementFieldRegExp = (field: FieldWithRequirement | null) => {
    let regExpString = field?.requirement;

    if (!regExpString) {
        return null;
    }

    if (!regExpString.startsWith('^')) {
        regExpString = `^${regExpString}`;
    }

    if (!regExpString.endsWith('$')) {
        regExpString = `${regExpString}$`;
    }

    return new RegExp(regExpString);
};

export const checkAreFieldsForConfirmationOfPayeeFilled = (
    values: FormValues,
    paymentDetailsRequirements?: PaymentDetailsRequirementsItem
) => {
    if (!paymentDetailsRequirements) {
        return false;
    }

    const {
        bankAccount: {
            bankAccountName,
            routing: { bankAccountNumber, sortCode },
        },
        legalEntity: { legalEntityType, companyName, firstName, lastName },
        paymentMethod,
    } = values;

    const bankAccountNumberRegExp = getRequirementFieldRegExp(paymentDetailsRequirements.accountNumberField);
    const sortCodeRegExp = getRequirementFieldRegExp(paymentDetailsRequirements.sortCodeField);

    const isBankAccountNumberValid =
        bankAccountNumber && bankAccountNumberRegExp && bankAccountNumberRegExp.test(bankAccountNumber);
    const isSortCodeValid = sortCode && sortCodeRegExp && sortCodeRegExp.test(sortCode);

    const hasBankDetails = isBankAccountNumberValid && isSortCodeValid;

    const isFasterPaymentsMethod = checkIsFasterPaymentsMethod(paymentMethod);
    const companyNameValue = isFasterPaymentsMethod ? bankAccountName : companyName;

    return legalEntityType === PaymentDetailsLegalEntityType.Company
        ? Boolean(companyNameValue && hasBankDetails)
        : Boolean(firstName && lastName && hasBankDetails);
};

export const checkIsFasterPaymentsMethod = (paymentMethod: string) => {
    return paymentMethod.toLowerCase().includes(fasterPaymentsPaymentMethod.toLowerCase());
};

export const checkIsChapsPaymentMethod = (paymentMethod: string) => {
    return paymentMethod.toLowerCase() === chapsPaymentMethod.toLowerCase();
};
